import { WEALTHICA_STORAGE_KEYS } from '../../constants-v2';

/**
 * Navigation state for Wealthica connection flow
 */
export interface Wealthica2NavigationState {
  /** URL to return to after OAuth redirect */
  returnUrl: string | null;
  /** Source of the return URL (manual, auto, etc.) */
  returnSource: string;
  /** Timestamp of the last state update */
  timestamp: number;
  /** Flag to preserve state during navigation */
  preserveState: boolean;
  /** Provider identifier */
  provider: string;
}

/**
 * Options for returning to previous location
 */
export interface ReturnOptions {
  /** Whether to show a toast message on return */
  showToast?: boolean;
  /** Toast message content */
  toastMessage?: string;
  /** Additional URL parameters to add to return URL */
  additionalParams?: Record<string, string>;
  /** Whether to use location.replace instead of location.href */
  replace?: boolean;
  /** Optional delay before navigation in milliseconds */
  delay?: number;
}

/**
 * Context type for Wealthica2 navigation provider
 */
export interface Wealthica2NavigationContextType {
  /** Current navigation state */
  navigationState: Wealthica2NavigationState;
  /** Update navigation state */
  setNavigationState: (state: Partial<Wealthica2NavigationState>) => void;
  /** Save current location for return after OAuth flow */
  saveCurrentLocation: (provider?: string, source?: string) => void;
  /** Return to previously saved location */
  returnToPreviousLocation: (options?: ReturnOptions) => void;
  /** Redirect to investments page */
  redirectToInvestments: (message?: string) => void;
  /** Flag indicating if we're returning from connection flow */
  isReturningFromConnection: boolean;
  /** Save OAuth state parameter */
  saveState: (state: string) => void;
  /** Get saved OAuth state parameter */
  getState: () => string | null;
  /** Save provider identifier */
  saveProvider: (provider: string) => void;
  /** Get saved provider identifier */
  getProvider: () => string | null;
  /** Detect if we're in a reload loop */
  detectReloadLoop: () => boolean;
  /** Clear all navigation data from storage */
  clearAllNavigationData: () => void;
  /** Save data to localStorage */
  saveToStorage: (key: string, value: string) => boolean;
  /** Get data from localStorage */
  getFromStorage: (key: string, defaultValue?: string | null) => string | null;
}

/**
 * Default navigation state
 */
export const defaultNavigationState: Wealthica2NavigationState = {
  returnUrl: null,
  returnSource: 'unknown',
  timestamp: Date.now(),
  preserveState: false,
  provider: 'wealthica2',
};

/**
 * Storage keys for navigation data
 */
export const NAVIGATION_STORAGE_KEYS = {
  STATE: WEALTHICA_STORAGE_KEYS.AUTH_STATE,
  RETURN_PATH: WEALTHICA_STORAGE_KEYS.RETURN_PATH,
  PROVIDER: 'WEALTHICA2_PROVIDER',
  RELOAD_COUNT: 'WEALTHICA2_RELOAD_COUNT',
  CALLBACK_PROCESSED: 'WEALTHICA2_CALLBACK_PROCESSED',
  CALLBACK_TIME: 'WEALTHICA2_CALLBACK_TIME',
};
